import React from "react";
import { List, Typography, Image, Input, Dropdown, Menu, Avatar } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import dayjs from "dayjs";
import PdfMessage from "./PdfMessage";

const formatMessageTime = (dateString) => {
  if (!dateString) return "";
  const messageDate = dayjs(dateString);
  const now = dayjs();
  const today = now.startOf("day");
  const yesterday = today.subtract(1, "day");

  if (now.diff(messageDate, "minute") < 1) return "Just now";
  if (messageDate.isSame(today, "day")) return `Today ${messageDate.format("h:mma")}`;
  if (messageDate.isSame(yesterday, "day"))
    return `Yesterday ${messageDate.format("h:mma")}`;
  return messageDate.format("dddd h:mma");
};

const stringToColor = (str) => {
  if (!str) return "#667085";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 65%, 55%)`;
};

const getInitial = (name) => name?.trim()?.charAt(0)?.toUpperCase() || "?";

const ChatMessage = ({
  msg,
  userId,
  activeUsers = [],
  editMsg,
  editingMessage,
  setEditingMessage,
  onSend,
  onRightClick,
  onEditAction,
  onDeleteAction,
  textareaRef,
}) => {
  const senderId =
    msg?.sender_id ?? msg?.sent_by?.id ?? msg?.sender?.id ?? msg?.user_id;
  const isMine =
    msg?.is_mine === true
      ? true
      : senderId == null
        ? false
        : userId == null
          ? false
          : String(userId) === String(senderId);
  const senderName =
    (msg?.sender_first_name ?? msg?.first_name ?? "") +
    " " +
    (msg?.sender_last_name ?? msg?.last_name ?? "");
  const isPartner = msg?.is_partner || msg?.sender_role === "Partner";
  const displayName = isMine ? "You" : senderName.trim() || "Anonymous";
  const timeLabel = formatMessageTime(msg?.created_at);

  const bubbleMaxWidth = 508;
  const bubbleMinHeight = 40;
  const bubbleBg = "#F3F4F6";
  const bubbleTextColor = "#111827";
  const bubbleBorder = "1px solid #E5E7EB";
  const bubblePadding = "8px 16px";
  const bubbleGap = 6;
  // sharp corner: mine -> top-right, others -> top-left
  const bubbleRadiusMine = "12px 0 12px 12px";
  const bubbleRadiusOther = "0 12px 12px 12px";

  const senderKey = senderId == null ? null : String(senderId);
  const normalizedName = displayName.trim().toLowerCase();
  const isSenderActive =
    !isMine &&
    (activeUsers || []).some((u) => {
      const activeId =
        u?.id ??
        u?.user_id ??
        u?.user__id ??
        u?.sent_by?.id ??
        u?.user?.id ??
        u?.sender_id;
      if (senderKey != null && activeId != null && String(activeId) === senderKey)
        return true;
      const activeName = `${u?.first_name ?? ""} ${u?.last_name ?? ""}`.trim();
      if (activeName && activeName.toLowerCase() === normalizedName) return true;
      if (u?.name && String(u.name).trim().toLowerCase() === normalizedName)
        return true;
      return false;
    });

  const menu = (
    <Menu>
      <Menu.Item
        key="edit"
        icon={<EditIcon />}
        onClick={() => onEditAction(msg?.id, msg?.message)}
      >
        Edit
      </Menu.Item>
      <Menu.Item
        key="delete"
        icon={<DeleteOutlined />}
        onClick={() => onDeleteAction(msg?.id)}
      >
        Delete
      </Menu.Item>
    </Menu>
  );

  const messageContent = (
    <List.Item
      id={msg?.id}
      key={msg?.id}
      className="chat-message-item"
      onContextMenu={(e) => isMine && onRightClick(e, msg)}
      style={{ position: "relative", border: "none", padding: "2px 0" }}
    >
      {msg?.message_type === "message" && (
        <div
          className="chat-message-row"
          style={{
            width: "100%",
            display: "flex",
            flexDirection: isMine ? "column" : "row",
            alignItems: isMine ? "flex-end" : "flex-start",
            padding: "4px 8px",
          }}
        >
          {!isMine && (
            <div
              className="chat-message-avatar-wrap"
              style={{ position: "relative", flex: "0 0 auto", marginTop: 2 }}
            >
              <Avatar
                className="chat-message-avatar"
                size={30}
                style={{
                  backgroundColor: stringToColor(displayName),
                  fontWeight: 900,
                  fontSize: 14,
                }}
              >
                {getInitial(displayName)}
              </Avatar>
              {isSenderActive && (
                <span
                  className="chat-message-active-dot"
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 9,
                    height: 9,
                    backgroundColor: "#16a34a",
                    borderRadius: "50%",
                    border: "1px solid white",
                  }}
                />
              )}
            </div>
          )}

          <div
            className="chat-message-body"
            style={{
              maxWidth: bubbleMaxWidth,
              width: "100%",
              marginLeft: isMine ? 0 : 10,
              display: "flex",
              flexDirection: "column",
              alignItems: isMine ? "flex-end" : "flex-start",
            }}
          >
            <div
              className="chat-message-meta"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: isMine ? "flex-end" : "flex-start",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <Typography.Text
                className="chat-message-sender"
                style={{
                  fontWeight: 600,
                  color: "#475467",
                  fontSize: 12,
                  lineHeight: "16px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {displayName}
              </Typography.Text>
              <Typography.Text
                className="chat-message-time"
                style={{
                  fontSize: 11,
                  color: "#9CA3AF",
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                }}
              >
                {timeLabel}
              </Typography.Text>
            </div>

          {editMsg === msg?.id ? (
            <Input.TextArea
              ref={textareaRef}
              autoSize={{ minRows: 1, maxRows: 6 }}
              value={editingMessage}
              onChange={(e) => setEditingMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
              autoFocus
              style={{
                backgroundColor: "#fff",
                border: bubbleBorder,
                borderRadius: isMine ? bubbleRadiusMine : bubbleRadiusOther,
                padding: bubblePadding,
                maxWidth: bubbleMaxWidth,
                width: "100%",
                minHeight: bubbleMinHeight,
              }}
            />
          ) : (
            <div
              className={`chat-message-bubble ${
                isMine ? "chat-message-bubble--mine" : "chat-message-bubble--other"
              }`}
              style={{
                backgroundColor: bubbleBg,
                color: bubbleTextColor,
                border: bubbleBorder,
                padding: bubblePadding,
                borderRadius: isMine ? bubbleRadiusMine : bubbleRadiusOther,
                maxWidth: bubbleMaxWidth,
                minHeight: bubbleMinHeight,
                display: "inline-flex",
                gap: bubbleGap,
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                wordBreak: "break-word",
                backgroundClip: "padding-box",
              }}
            >
              <Typography.Text
                className="chat-message-bubble-text"
                style={{
                  fontSize: 14,
                  lineHeight: "20px",
                  display: "block",
                  color: bubbleTextColor,
                }}
              >
                {msg?.message}
              </Typography.Text>
            </div>
          )}
          </div>
        </div>
      )}
      {msg?.message_type === "info" && (
        <div
          className="chat-message-info"
          style={{
            textAlign: "center",
            width: "100%",
            padding: "4px 0",
          }}
        >
          <Typography.Text
            className="chat-message-info-text"
            style={{
              fontSize: 12,
              color: "#9CA3AF",
              backgroundColor: "#F3F4F6",
              padding: "2px 10px",
              borderRadius: 10,
            }}
          >
            {isMine
              ? "You"
              : `${msg?.first_name ?? ""} ${msg?.last_name ?? ""}`.trim()}{" "}
            {msg?.message === "user_joined"
              ? "joined"
              : msg?.message === "user_left"
              ? "left"
              : "System"}
          </Typography.Text>
        </div>
      )}
      {msg?.message_type === "attachment" && (
        <div
          className="chat-message-row"
          style={{
            width: "100%",
            display: "flex",
            flexDirection: isMine ? "column" : "row",
            alignItems: isMine ? "flex-end" : "flex-start",
            padding: "4px 8px",
          }}
        >
          {!isMine && (
            <div
              className="chat-message-avatar-wrap"
              style={{ position: "relative", flex: "0 0 auto", marginTop: 2 }}
            >
              <Avatar
                className="chat-message-avatar"
                size={30}
                style={{
                  backgroundColor: stringToColor(displayName),
                  fontWeight: 900,
                  fontSize: 14,
                }}
              >
                {getInitial(displayName)}
              </Avatar>
              {isSenderActive && (
                <span
                  className="chat-message-active-dot"
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 9,
                    height: 9,
                    backgroundColor: "#16a34a",
                    borderRadius: "50%",
                    border: "1px solid white",
                  }}
                />
              )}
            </div>
          )}

          <div
            className="chat-message-body"
            style={{
              maxWidth: bubbleMaxWidth,
              width: "100%",
              marginLeft: isMine ? 0 : 10,
              display: "flex",
              flexDirection: "column",
              alignItems: isMine ? "flex-end" : "flex-start",
            }}
          >
            <div
              className="chat-message-meta"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: isMine ? "flex-end" : "flex-start",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <Typography.Text
                className="chat-message-sender"
                style={{
                  fontWeight: 600,
                  color: "#475467",
                  fontSize: 12,
                  lineHeight: "16px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {displayName}
              </Typography.Text>
              <Typography.Text
                className="chat-message-time"
                style={{
                  fontSize: 11,
                  color: "#9CA3AF",
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                }}
              >
                {timeLabel}
              </Typography.Text>
            </div>

            {msg?.attachment?.type?.startsWith("image/") ? (
              <Image
                className="chat-message-attachment-image"
                src={msg?.attachment_url}
                alt="attachment"
                style={{ maxWidth: 200, borderRadius: 8, objectFit: "cover" }}
              />
            ) : msg?.attachment?.type === "application/pdf" ? (
              <PdfMessage
                url={msg?.attachment_url}
                name={msg?.attachment?.path?.split("/").pop()}
                isPartner={isPartner}
              />
            ) : (
              <a
                href={msg?.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download file
              </a>
            )}
          </div>
        </div>
      )}
    </List.Item>
  );

  if (isMine) {
    return (
      <Dropdown overlay={menu} trigger={["contextMenu"]}>
        {messageContent}
      </Dropdown>
    );
  }
  return messageContent;
};

export default ChatMessage;
